import { DocsFrontmatter, DocsParsed } from "features/Docs/types/docs.types"

/**
 * Hand-rolled parser for the restricted frontmatter schema used by docs pages.
 *
 * Between a leading pair of `---` fences we accept:
 *   title:       "…"                (required, quoted or bare)
 *   description: "…"                (required, quoted or bare)
 *   keywords:    [a, b, "c d"]      (optional flow-style list of strings)
 *
 * Anything else in the block is ignored. Files without frontmatter are
 * tolerated: their meta fields come back null/empty and the body is returned
 * unchanged, so a page still renders using its manifest title.
 */

const EMPTY_META: DocsFrontmatter = {
  title: null,
  description: null,
  keywords: [],
}

/** Strip a single layer of matching single/double quotes; otherwise trim. */
const unquote = (value: string): string => {
  const trimmed = value.trim()
  if (
    trimmed.length >= 2 &&
    ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'")))
  ) {
    return trimmed.slice(1, -1)
  }
  return trimmed
}

/** Parse a flow-style YAML list: `[a, b, "c d"]` → ["a", "b", "c d"]. */
const parseFlowList = (value: string): string[] => {
  const inner = value.trim().replace(/^\[/, "").replace(/\]$/, "")
  const items: string[] = []
  let current = ""
  let quote: string | null = null

  for (const char of inner) {
    if (quote) {
      if (char === quote) quote = null
      else current += char
    } else if (char === '"' || char === "'") {
      quote = char
    } else if (char === ",") {
      const item = current.trim()
      if (item) items.push(item)
      current = ""
    } else {
      current += char
    }
  }
  const last = current.trim()
  if (last) items.push(last)
  return items
}

/**
 * Parse frontmatter + body from a raw markdown string.
 */
export const parseFrontmatter = (raw: string): DocsParsed => {
  // Strip a UTF-8 BOM if the file carries one.
  const text = raw.replace(/^\uFEFF/, "")
  const fence = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n)?/.exec(text)

  if (!fence) {
    return { meta: { ...EMPTY_META }, body: text }
  }

  const block = fence[1]
  const body = text.slice(fence[0].length)
  const meta: DocsFrontmatter = { title: null, description: null, keywords: [] }

  block.split(/\r?\n/).forEach((line) => {
    const entry = /^([A-Za-z_][\w-]*)\s*:\s*(.*)$/.exec(line)
    if (!entry) return
    const key = entry[1].toLowerCase()
    const value = entry[2]

    if (key === "title") {
      const parsed = unquote(value)
      meta.title = parsed.length > 0 ? parsed : null
    } else if (key === "description") {
      const parsed = unquote(value)
      meta.description = parsed.length > 0 ? parsed : null
    } else if (key === "keywords") {
      meta.keywords = parseFlowList(value)
    }
    // Any other field is ignored.
  })

  return { meta, body }
}
